import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { ColumnDef } from '../common/tableFeatures';
import { clipped, dateSortValue, noActions, shortDate } from '../common/tableCells';
import type { ClientVersion } from '@store/api/clientUpdatesApi';

interface ClientVersionColumnHandlers {
  onDeploy: (version: string) => void;
  onDelete: (version: string) => void;
}

/** TanStack column definitions for the client-version table. Five columns, so
 *  no column selector - sorting is the useful affordance here. */
export const createClientVersionColumns = ({
  onDeploy,
  onDelete,
}: ClientVersionColumnHandlers): ColumnDef<ClientVersion>[] => [
  {
    id: 'version',
    accessorKey: 'version',
    header: 'Version',
    cell: ({ row }) => {
      const version = row.original;
      const isDeleted = version.is_deleted || false;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {version.version}
          </Typography>
          {version.is_critical && !isDeleted && (
            <Chip label="Critical" color="error" size="small" />
          )}
          {isDeleted && <Chip label="Deleted" size="small" variant="outlined" />}
        </Box>
      );
    },
    size: 180,
  },
  {
    id: 'release_date',
    accessorFn: (row) => dateSortValue(row.release_date),
    header: 'Release Date',
    cell: ({ row }) => shortDate(row.original.release_date),
    size: 130,
  },
  {
    id: 'file_size',
    accessorFn: (row) => row.file_size ?? 0,
    header: 'File Size',
    // Two decimals of MB, and 'N/A' for a missing size, as this table has
    // always shown them.
    cell: ({ row }) => {
      const { is_deleted: isDeleted, file_size: size } = row.original;
      if (isDeleted || !size) return 'N/A';
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    },
    size: 110,
  },
  {
    id: 'changelog',
    accessorKey: 'changelog',
    header: 'Changelog',
    cell: ({ row }) => {
      const version = row.original;
      return version.is_deleted
        ? clipped('Deleted version - no files available', 300)
        : clipped(version.changelog, 300);
    },
    size: 320,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const version = row.original;
      if (version.is_deleted) {
        return noActions();
      }
      return (
        <>
          <Tooltip title="Deploy to Devices">
            <IconButton
              size="small"
              color="primary"
              onClick={() => onDeploy(version.version)}
              aria-label={`Deploy version ${version.version} to devices`}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Version">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(version.version)}
              aria-label={`Delete version ${version.version}`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      );
    },
    enableSorting: false,
    size: 120,
    meta: { sticky: 'right' },
  },
];
